import { Shape }                 from '@tokamap/core';
import { observer }              from 'mobx-react-lite';
import React                     from 'react';
import Button                    from '@widesk-ui/components/Button';
import Popover                   from '@widesk-ui/components/Popover';
import View                      from '@widesk-ui/components/View';
import useTheme                  from '@widesk-ui/hooks/useTheme';
import useDesignTokens           from '@/hooks/useDesignTokens';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import projectShapesStore        from '@/TokamapEditor/stores/projectShapesStore';
import compareMapsByPosition     from '@/TokamapEditor/helpers/compareMapsByPosition';

const zonesLabel = (count: number) => `${count || '0'} zone${count > 1 ? 's' : ''}`;

type ZoneCountButtonProps = {
	filter: (shape: Shape) => boolean;
	/** Reçoit le total, pour accorder. */
	suffix: (count: number) => string;
};

/**
 * La carte ouverte se compte en direct (`allShapes`), les autres depuis leur dernier état enregistré
 * (`projectShapesStore`). Le bouton ne met en évidence que la carte ouverte, seule dessinée.
 */
const ZoneCountButton = observer((props: ZoneCountButtonProps) => {
	const theme = useTheme();
	const tokens = useDesignTokens();
	const [open, setOpen] = React.useState(false);
	const projectEditor = currentProjectEditorStore.get;
	const currentMapId = String(projectEditor.currentMap.id);

	const currentShapes = currentEditorStore.allShapes.filter(props.filter);

	const rows = projectEditor.mapCollection.models
		.filter(map => !!map.id)
		.map(map => {
			const isCurrent = String(map.id) === currentMapId;

			return {
				map,
				isCurrent,
				count: isCurrent ? currentShapes.length : projectShapesStore.shapesOfMap(map.id).filter(props.filter).length,
			};
		})
		.sort((a, b) => compareMapsByPosition(a.map, b.map));

	const total = rows.reduce((sum, row) => sum + row.count, 0);
	const mapsWithShapes = rows.filter(row => row.count);

	const highlightCurrent = () => currentEditorStore.highlightAndSelectShapes(currentShapes.map(shape => shape.id));

	const openRow = (row: typeof rows[number]) => {
		setOpen(false);

		if (row.isCurrent) return highlightCurrent();

		currentEditorStore.requestHighlightOnMap(row.map.id, props.filter);
		projectEditor.setCurrentMap(row.map);
	};

	const button = (
		<Button
			onClick={e => {
				e.stopPropagation();
				highlightCurrent();
			}}
			size="small"
			disabled={!currentShapes.length}
			htmlType="button"
			type="dashed"
			style={{ fontSize: theme.sizeSM }}
		>
			{zonesLabel(total)} {props.suffix(total)}
		</Button>
	);

	if (!mapsWithShapes.length) return button;

	return (
		<Popover
			trigger="hover"
			placement="right"
			autoAdjustOverflow={{ shiftX: true }}
			open={open}
			onOpenChange={setOpen}
			styles={{ body: { padding: 4 } }}
			content={(
				<View style={{ minWidth: 200 }} onClick={e => e.stopPropagation()}>
					{mapsWithShapes.map(row => (
						<View
							key={String(row.map.id)}
							row
							centerV
							spread
							gap="sm"
							px="sm"
							py={6}
							br={6}
							pointer
							hover={{ bg: tokens.surface.subtle }}
							onClick={() => openRow(row)}
						>
							<span>
								{row.map.label}
								{row.isCurrent && <span style={{ color: tokens.text.muted }}> (carte ouverte)</span>}
							</span>

							<b>{row.count}</b>
						</View>
					))}
				</View>
			)}
		>
			{/* Un bouton désactivé n'émet pas de survol, et son clic ne doit pas ouvrir la ligne. */}
			<span style={{ display: 'inline-block' }} onClick={e => e.stopPropagation()}>
				{button}
			</span>
		</Popover>
	);
});

export default ZoneCountButton;
